import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const navLinks = [
  { to: '/', text: 'Home' },
  { to: '/fleet', text: 'Fleet' },
  { to: '/services', text: 'Services' },
  { to: '/booking', text: 'Booking' },
  { to: '/reviews', text: 'Reviews' },
  { to: '/contact', text: 'Contact' },
  { to: '/about', text: 'About' },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const activeLinkStyle = {
    color: '#D4AF37',
    textDecoration: 'underline',
    textUnderlineOffset: '8px',
    textDecorationThickness: '2px',
  };

  return (
    <header className={`${isScrolled ? 'bg-brand-dark/95 shadow-2xl shadow-brand-gold/20' : 'bg-brand-dark/90 shadow-lg shadow-brand-gold/10'} backdrop-blur-md sticky top-0 z-50 transition-all duration-300 border-b border-brand-gold/20`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          <Link to="/" className="text-4xl font-bold font-serif bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold bg-clip-text text-transparent tracking-wider drop-shadow-lg hover:scale-105 transition-transform duration-300">
            Akiblackcar
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-10">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
                className="text-lg font-semibold text-white hover:text-brand-gold transition-all duration-300 hover:scale-105 drop-shadow-sm relative group"
              >
                {link.text}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-gold to-yellow-400 transition-all duration-300 group-hover:w-full"></span>
              </NavLink>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button 
              onClick={() => setIsOpen(!isOpen)} 
              variant="ghost" 
              size="icon"
              className="text-brand-gold hover:text-white hover:bg-brand-gold/20 transition-all duration-300"
            >
              {isOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
            </Button>
          </div>
        </div>
      </div>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="lg:hidden bg-gradient-to-br from-brand-charcoal via-gray-900 to-brand-dark border-t border-brand-gold/20"
        >
          <nav className="flex flex-col items-center space-y-6 py-10">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                style={({ isActive }) => (isActive ? activeLinkStyle : undefined)}
                className="text-2xl font-semibold text-white hover:text-brand-gold transition-all duration-300 hover:scale-105 drop-shadow-lg relative group"
              >
                {link.text}
                <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-brand-gold to-yellow-400 transition-all duration-300 group-hover:w-full"></span>
              </NavLink>
            ))}
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Header;